import { setAccessToken, v14 } from '@readcloud/api-client';
import { DecodedToken, Role, Scope, defineRules } from '@readcloud/casl';
import { AbilityContext, tokenContext } from '@readcloud/casl-react';
import { authActions } from '@readcloud/state';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { trackEvent } from './analytics';
import { recordError } from './rum';
import { decodeJwt } from '@readcloud/common';
import { listen } from '@tauri-apps/api/event';
import { authenticate, openUrl } from './inAppBrowser';
import { isDesktopPlatform } from './deviceUtils';
import {
  OidcClient,
  User,
  SigninResponse,
  WebStorageStateStore,
} from 'oidc-client-ts';
import {
  clearAllTables,
  setOnAuthFailure,
  subscribeToOnlineState,
} from '@readcloud/fetch-wrapper';
import { isWincApp } from './isWincApp';

// Get the appropriate callback scheme for OAuth
// Must match the reverse-domain scheme used in redirect_uri (see environment.ts)
const getCallbackScheme = (): string => {
  return isWincApp() ? 'com.winc.ebooks' : 'com.readcloud.nativeapp';
};

export type AuthContextUser = {
  accessToken: string;
  email: string;
  firstName: string;
  lastName: string;
  institution: string;
  permissions: string[];
  reseller?: string;
  sub: string;
  id: string;
  clientId: string;
  role: string;
  scope: string;
  userScope?: string;
};

export type AuthContext = {
  user?: AuthContextUser;
  logout: () => void;
};

// Custom auth state interface to match react-oidc-context
interface CustomAuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  /**
   * True when the access token has expired and the IdP could not be reached
   * to renew it (typically offline). The user is kept so downloaded content
   * still works; the UI offers "sign in again" and we retry automatically
   * when connectivity returns. Never true when the IdP *rejected* the refresh
   * — that clears the user and sends them to login instead.
   */
  sessionExpired: boolean;
  signinRedirect: (args?: { state?: any }) => Promise<void>;
  signinSilent: () => Promise<boolean>;
  signoutRedirect: (args?: {
    post_logout_redirect_uri?: string;
    onBeforeRedirect?: () => void;
  }) => Promise<void>;
  signoutSilent: (args?: { onBeforeRedirect?: () => void }) => Promise<void>;
  signinPopup: () => Promise<void>;
  activeNavigator: string | null;
  isLoggingOut: boolean;
}

const authContext = createContext<null | AuthContext>(null);
const customAuthContext = createContext<CustomAuthState | null>(null);

function decodeJwtAugmented(jwtString: string) {
  try {
    const payload = decodeJwt(jwtString).payload;
    payload['accessToken'] = jwtString;
    payload['id'] = payload.sub;
    return payload as DecodedToken;
  } catch (e) {
    recordError(e);
    return null;
  }
}

type RefreshOutcome =
  | 'refreshed'
  | 'rejected' // IdP answered with an OAuth error (invalid_grant …) — session is over
  | 'unavailable' // transport failure: offline, timeout, 5xx
  | 'no-refresh-token'
  | 'superseded'; // user logged out / re-logged in while the refresh was in flight

/**
 * oidc-client-ts throws an ErrorResponse (with an `error` code such as
 * "invalid_grant") when the token endpoint answers with an OAuth error;
 * transport failures surface as TypeErrors / plain Errors without one.
 */
function isServerAuthError(err: unknown): boolean {
  return !!err && typeof err === 'object' && 'error' in err;
}

type OidcConfig = {
  authority: string;
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  post_logout_redirect_uri: string;
  silent_redirect_uri?: string;
  automaticSilentRenew?: boolean;
  extraQueryParams?: Record<string, string>;
};

/**
 * Generate the storage key in the standard oidc-client-ts format.
 * This matches the key format used by UserManager: `oidc.user:{authority}:{client_id}`
 * Note: WebStorageStateStore adds the "oidc." prefix automatically, so we only need `user:{authority}:{client_id}`
 */
function getOidcStorageKey(authority: string, clientId: string): string {
  return `user:${authority}:${clientId}`;
}

// Key for persisting the force-login-prompt flag across app restarts
const FORCE_LOGIN_KEY = 'rc_force_login_prompt';

/**
 * Custom auth provider using oidc-client-ts directly
 */
function CustomAuthProvider(props: {
  children: React.ReactNode;
  oidcConfig: OidcConfig;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [activeNavigator, setActiveNavigator] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // When true, the next signinRedirect will include prompt=login to force
  // the IdP to show the login form (used after logout to prevent auto-login).
  // Persisted in localStorage so it survives app restarts (e.g. logout, close app, reopen).
  const forceLoginPromptRef = useRef(
    localStorage.getItem(FORCE_LOGIN_KEY) === 'true'
  );

  const isTauri = (window as any).__TAURI__ !== undefined;

  // Create OidcClient instance
  const oidcClient = useMemo(() => {
    // Test network connectivity to the authority
    const testNetworkConnectivity = async () => {
      try {
        const response = await fetch(
          `${props.oidcConfig.authority}/.well-known/openid-configuration`
        );
      } catch (error) {
        console.error('Network test failed:', JSON.stringify(error));
      }
    };

    // Run the test
    testNetworkConnectivity();

    // Use sessionStorage for web (tab-isolated, cleared on browser close)
    // Use localStorage for Tauri apps (persist across app restarts for offline usage)
    // Web apps use silent signin via iframe to share sessions across tabs
    const storage = isTauri ? window.localStorage : window.sessionStorage;

    return new OidcClient({
      authority: props.oidcConfig.authority,
      client_id: props.oidcConfig.client_id,
      redirect_uri: props.oidcConfig.redirect_uri,
      response_type: props.oidcConfig.response_type,
      scope: props.oidcConfig.scope,
      post_logout_redirect_uri: props.oidcConfig.post_logout_redirect_uri,
      stateStore: new WebStorageStateStore({ store: storage }),
      // Provide metadata directly to bypass network requests on Android 14
      metadata: {
        authorization_endpoint: `${props.oidcConfig.authority}/auth`,
        token_endpoint: `${props.oidcConfig.authority}/token`,
        userinfo_endpoint: `${props.oidcConfig.authority}/userinfo`,
        end_session_endpoint: `${props.oidcConfig.authority}/session/end`,
        jwks_uri: `${props.oidcConfig.authority}/jwks`,
        issuer: props.oidcConfig.authority,
      },
    });
  }, [props.oidcConfig]);

  // Storage key in the standard oidc-client-ts format: oidc.user:{authority}:{client_id}
  const storageKey = getOidcStorageKey(
    props.oidcConfig.authority,
    props.oidcConfig.client_id
  );

  // ---------------------------------------------------------------------------
  // Token refresh — single-flight.
  //
  // The IdP rotates refresh tokens on every use and treats a second use of a
  // consumed token as theft: the whole grant is revoked and the user is signed
  // out everywhere. Every caller (startup, proactive renewal, 401 handler,
  // back-online retry) therefore goes through refreshTokens(), which shares one
  // in-flight request and always reads the *latest* refresh token from a ref
  // rather than from a possibly stale closure.
  // ---------------------------------------------------------------------------
  const userRef = useRef<User | null>(null);
  const sessionExpiredRef = useRef(false);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    sessionExpiredRef.current = sessionExpired;
  }, [sessionExpired]);
  const refreshPromiseRef = useRef<Promise<RefreshOutcome> | null>(null);

  const refreshTokens = useCallback(
    (reason: string): Promise<RefreshOutcome> => {
      if (refreshPromiseRef.current) {
        console.log(
          `[AuthProvider] Refresh already in flight, joining it (${reason})`
        );
        return refreshPromiseRef.current;
      }

      const run = (async (): Promise<RefreshOutcome> => {
        const current = userRef.current;
        if (!current?.refresh_token) return 'no-refresh-token';

        console.log(`[AuthProvider] Refreshing tokens (${reason})`);
        try {
          const response = await oidcClient.useRefreshToken({
            state: {
              refresh_token: current.refresh_token,
              scope: current.scope,
              session_state: current.session_state,
              profile: current.profile,
            },
          });

          if (userRef.current !== current) {
            // Logged out or signed in afresh while we were waiting; the result
            // belongs to a session that no longer exists.
            console.warn(
              `[AuthProvider] Discarding refresh result, user changed (${reason})`
            );
            return 'superseded';
          }

          const refreshed = new User(response);
          // Expose the new token to any concurrent caller and persist it before
          // React re-renders, so nothing can pick up the consumed one.
          userRef.current = refreshed;
          await oidcClient.settings.stateStore.set(
            storageKey,
            refreshed.toStorageString()
          );
          setUser(refreshed);
          setSessionExpired(false);
          console.log(`[AuthProvider] Token refresh succeeded (${reason})`);
          return 'refreshed';
        } catch (err) {
          if (isServerAuthError(err)) {
            console.warn(
              `[AuthProvider] Refresh token rejected by server (${reason}):`,
              err
            );
            return 'rejected';
          }
          console.warn(
            `[AuthProvider] Token endpoint unreachable (${reason}):`,
            err
          );
          return 'unavailable';
        }
      })();

      const tracked = run.finally(() => {
        refreshPromiseRef.current = null;
      });
      refreshPromiseRef.current = tracked;
      return tracked;
    },
    [oidcClient, storageKey]
  );

  // Drop the local session so AuthContextProvider sends the user to login.
  const clearUserForReauth = useCallback(async () => {
    console.log('[AuthProvider] Clearing user for re-auth');
    userRef.current = null;
    setUser(null);
    setSessionExpired(false);
    await oidcClient.settings.stateStore.remove(storageKey);
  }, [oidcClient, storageKey]);

  // Process OAuth callback from external URL
  // Returns the original URL from state (if any) for redirect after login
  const processSigninCallbackFromUrl = useCallback(
    async (callbackUrl: string): Promise<string | null> => {
      try {
        setIsLoading(true);
        setActiveNavigator('signin-callback');

        const response = await oidcClient.processSigninResponse(callbackUrl);

        const user = new User(response);

        userRef.current = user;
        setUser(user);
        setError(null);
        setSessionExpired(false);

        // Login succeeded - clear the force login prompt flag
        forceLoginPromptRef.current = false;
        localStorage.removeItem(FORCE_LOGIN_KEY);

        // Store user in storage
        await oidcClient.settings.stateStore.set(
          storageKey,
          user.toStorageString()
        );

        // Return the original URL from state (if provided)
        // In oidc-client-ts, custom state data is stored in response.userState, not response.state
        // response.state is the raw CSRF token string, userState contains our custom data

        const originalUrl = (response.userState as any)?.returnUrl;

        return originalUrl || null;
      } catch (err) {
        console.error('Error processing signin callback:', err);
        recordError(err);

        if (userRef.current && !sessionExpiredRef.current) {
          // A callback failed while a working session is already established —
          // e.g. a stale/replayed callback URL delivered as a deep link. Surfacing
          // that as an auth error would throw a "session expired" dialog over a
          // perfectly healthy session, so just log it.
          console.warn(
            '[AuthProvider] Ignoring failed signin callback: user already signed in'
          );
          return null;
        }

        setError(
          err instanceof Error ? err : new Error('Authentication failed')
        );
        return null;
      } finally {
        setIsLoading(false);
        setActiveNavigator(null);
      }
    },
    [!!oidcClient]
  );

  // Silent signin using hidden iframe (web only)
  // Uses prompt=none to check if user has active session on OIDC provider
  const signinSilent = useCallback(async (): Promise<boolean> => {
    if (!props.oidcConfig.silent_redirect_uri) {
      console.log('No silent_redirect_uri configured, skipping silent signin');
      return false;
    }

    try {
      console.log('Attempting silent signin...');
      setActiveNavigator('signin-silent');

      // Create signin request with prompt=none and silent redirect URI
      const req = await oidcClient.createSigninRequest({
        redirect_uri: props.oidcConfig.silent_redirect_uri,
        prompt: 'none',
        extraQueryParams: props.oidcConfig.extraQueryParams,
      });

      // Create hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');

      const timeoutMs = 10000; // 10 second timeout

      const result = await new Promise<string>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Silent signin timed out'));
        }, timeoutMs);

        // Listen for message from iframe
        const messageHandler = (event: MessageEvent) => {
          // Verify origin matches our app
          if (event.origin !== window.location.origin) return;

          if (event.data?.type === 'silent_signin_callback') {
            clearTimeout(timeout);
            window.removeEventListener('message', messageHandler);
            if (event.data.url) {
              resolve(event.data.url);
            } else if (event.data.error) {
              reject(new Error(event.data.error));
            }
          }
        };

        window.addEventListener('message', messageHandler);

        iframe.onload = () => {
          // Give the iframe a moment to postMessage back
          // If it doesn't, the timeout will handle it
        };

        iframe.onerror = () => {
          clearTimeout(timeout);
          window.removeEventListener('message', messageHandler);
          reject(new Error('Failed to load silent signin iframe'));
        };

        document.body.appendChild(iframe);
        iframe.src = req.url;
      }).finally(() => {
        // Clean up iframe
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      });

      // Process the callback URL from iframe
      const response = await oidcClient.processSigninResponse(result);
      const user = new User(response);

      userRef.current = user;
      setUser(user);
      setError(null);
      setSessionExpired(false);

      // Store user in storage
      await oidcClient.settings.stateStore.set(
        storageKey,
        user.toStorageString()
      );

      console.log('Silent signin successful');
      return true;
    } catch (err) {
      console.log('Silent signin failed:', err);
      // Silent signin failed - this is expected if no session exists
      // Don't set error state, just return false so caller can fall back to redirect
      return false;
    } finally {
      setActiveNavigator(null);
    }
  }, [
    oidcClient,
    props.oidcConfig.silent_redirect_uri,
    props.oidcConfig.extraQueryParams,
    storageKey,
  ]);

  // Listen for OAuth callback events (for Tauri custom protocol)
  useEffect(() => {
    if (!isTauri) return;

    const unlisten = listen('oauth-callback', async (event) => {
      const callbackUrl = event.payload as string;
      console.log('OAuth callback received:', callbackUrl);

      // Check if this is a logout completion redirect
      const urlObj = new URL(callbackUrl);

      // Check if the callback URL contains /logout path
      if (urlObj.pathname.includes('/logout')) {
        console.log('Logout callback detected via /logout path');

        // Double check that the user is logged out
        setUser(null);
        await oidcClient.settings.stateStore.remove(storageKey);

        // Complete the logout process
        setIsLoggingOut(false);
        setActiveNavigator(null);

        // Clear the logout timeout since logout completed successfully
        if (logoutTimeoutRef.current) {
          clearTimeout(logoutTimeoutRef.current);
          logoutTimeoutRef.current = null;
        }

        // Clean up sessionStorage
        sessionStorage.removeItem('logout_in_progress');
        sessionStorage.removeItem('logout_timestamp');

        console.log('Logout completed via OAuth callback with /logout path');
        return;
      }

      // Process the OAuth signin callback
      console.log('Processing signin callback');
      processSigninCallbackFromUrl(callbackUrl);
    });

    return () => {
      unlisten.then((fn) => fn());
    };
  }, [isTauri]);

  // Listen for OAuth callback route on web (not Tauri)
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (!isTauri && location.pathname === '/authentication/callback') {
      // Process the OIDC callback
      processSigninCallbackFromUrl(window.location.href)
        .then((returnUrl) => {
          // The returnUrl comes from the OAuth state (userState.returnUrl)
          // This is properly round-tripped through the OAuth flow
          const redirectTo = returnUrl || '/';
          console.log('Redirecting after login to:', redirectTo);
          history.replace(redirectTo);
        })
        .catch((err) => {
          // Handle error (show message, etc)
          console.error('OIDC callback error:', err);
        });
    }

    // Handle silent callback - this runs in a hidden iframe
    // It posts the callback URL back to the parent window
    if (!isTauri && location.pathname === '/authentication/silent_callback') {
      const callbackUrl = window.location.href;
      if (window.parent && window.parent !== window) {
        // We're in an iframe, post message to parent
        window.parent.postMessage(
          { type: 'silent_signin_callback', url: callbackUrl },
          window.location.origin
        );
      }
    }
    // Only run on mount and when location changes
  }, [isTauri, location.pathname, processSigninCallbackFromUrl, history]);

  // Clear logout timeout when user successfully logs in
  useEffect(() => {
    if (user && logoutTimeoutRef.current) {
      // User successfully logged in, clear the logout timeout
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
      console.log('Logout timeout cleared - user logged in successfully');
    }
  }, [user]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
        logoutTimeoutRef.current = null;
      }
    };
  }, []);

  // A 401 from an API call (fetch-wrapper has already confirmed we're online):
  // refresh once, and only force re-login if the IdP rejects the refresh token.
  useEffect(() => {
    if (!isTauri) return;

    setOnAuthFailure(async () => {
      const outcome = await refreshTokens('401');
      if (outcome === 'rejected' || outcome === 'no-refresh-token') {
        await clearUserForReauth();
      }
      // 'unavailable' / 'superseded': leave things as they are; the proactive
      // timer or the next 401 will try again.
    });

    return () => {
      setOnAuthFailure(null);
    };
  }, [isTauri, refreshTokens, clearUserForReauth]);

  // Load the stored user once on mount (except on callback/logout routes).
  // Deliberately not re-run on route changes: each run may issue a refresh,
  // and with rotation two refreshes of the same token revoke the whole grant.
  useEffect(() => {
    if (
      location.pathname.includes('/authentication/callback') ||
      location.pathname.includes('/logout')
    ) {
      setIsLoading(false);
      return;
    }

    const loadUser = async () => {
      try {
        setIsLoading(true);

        const stored = await oidcClient.settings.stateStore.get(storageKey);
        if (!stored) {
          console.log('No stored user found');
          return;
        }

        const storedUser = User.fromStorageString(stored);
        const stillValid =
          !!storedUser.expires_at && storedUser.expires_at * 1000 > Date.now();

        if (stillValid) {
          userRef.current = storedUser;
          setUser(storedUser);
          setError(null);
          return;
        }

        console.log('Token expired, attempting refresh');
        setActiveNavigator('token-refresh');
        userRef.current = storedUser; // refreshTokens() reads the token from here
        const outcome = await refreshTokens('startup');

        switch (outcome) {
          case 'refreshed':
          case 'superseded':
            break;

          case 'rejected':
            // Online, and the IdP said no (grant expired, token revoked or
            // reused): straight back to login.
            await clearUserForReauth();
            break;

          case 'unavailable':
          case 'no-refresh-token':
            if (isTauri) {
              // Couldn't reach the IdP (typically offline). Keep the expired
              // user so downloaded content still works, flag the session so
              // the UI can offer "sign in again", and retry automatically
              // when connectivity returns (see effect below).
              console.log(
                'Tauri app - keeping expired user for offline access'
              );
              setUser(storedUser);
              setError(null);
              setSessionExpired(true);
            } else {
              await clearUserForReauth();
            }
            break;
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load user'));
        recordError(err);
      } finally {
        setActiveNavigator(null);
        setIsLoading(false);
      }
    };

    loadUser();
    // location.pathname is intentionally not a dependency (see comment above).
  }, [oidcClient, storageKey, isTauri, refreshTokens, clearUserForReauth]);

  // Proactive silent token renewal — refresh shortly before the access token
  // expires so API calls never hit a 401 while the refresh token is valid.
  useEffect(() => {
    if (!user?.expires_at || !user.refresh_token) return;

    const expiresAtMs = user.expires_at * 1000;
    // Refresh 60 seconds before expiry (or immediately if already past that)
    const renewInMs = Math.max(expiresAtMs - Date.now() - 60_000, 0);

    console.log(
      `[AuthProvider] Scheduling silent token renewal in ${Math.round(
        renewInMs / 1000
      )}s`
    );

    const timerId = setTimeout(async () => {
      const outcome = await refreshTokens('proactive');
      if (outcome === 'rejected') {
        // Online and rejected: the session is over. Go to login now rather
        // than letting the next API call fail.
        await clearUserForReauth();
      }
      // 'unavailable': keep using the current token; the 401 handler or the
      // back-online retry will pick it up.
    }, renewInMs);

    return () => clearTimeout(timerId);
  }, [
    user?.expires_at,
    user?.refresh_token,
    refreshTokens,
    clearUserForReauth,
  ]);

  // While the session is flagged expired (IdP unreachable at startup), retry
  // the refresh as soon as we look online again. If the IdP then rejects the
  // refresh token, go to login — "online means redirect, not a dialog".
  useEffect(() => {
    if (!isTauri || !sessionExpired) return;

    const retry = async (trigger: string) => {
      const outcome = await refreshTokens(`back-online:${trigger}`);
      // Only a definitive rejection ends the session here; a foreground event
      // doesn't prove we're online, so 'unavailable' just means "try later".
      if (outcome === 'rejected') {
        await clearUserForReauth();
      }
    };

    // subscribeToOnlineState replays the current state synchronously; skip
    // that first call — startup just tried and failed.
    let initialCall = true;
    const unsubscribe = subscribeToOnlineState((online) => {
      if (initialCall) {
        initialCall = false;
        return;
      }
      if (online) retry('fetch-wrapper');
    });
    const onOnline = () => retry('window.online');
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry('foreground');
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubscribe();
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isTauri, sessionExpired, refreshTokens, clearUserForReauth]);

  // Signin redirect function
  const signinRedirect = useCallback(
    async (args?: { state?: any }) => {
      if (location.pathname === '/authentication/callback') return;
      try {
        setActiveNavigator('signin-redirect');
        setError(null);
        console.log('signin redirect', isTauri);
        if (isTauri) {
          console.log('Creating signin request...');
          let req;
          try {
            const extraParams = { ...props.oidcConfig.extraQueryParams };
            if (forceLoginPromptRef.current) {
              extraParams.prompt = 'login';
              // Don't reset here - only reset after successful login
              // so that retries (e.g. "Try Again" after cancellation) still force the login prompt
            }
            req = await oidcClient.createSigninRequest({
              state: args?.state || {},
              extraQueryParams: extraParams,
            });
            console.log('Tauri signin request:', req);
            console.log('opening url:', req.url);
          } catch (createReqError) {
            console.error('Error creating signin request:', createReqError);
            console.error(
              'Error details:',
              JSON.stringify({
                message:
                  createReqError instanceof Error
                    ? createReqError.message
                    : 'Unknown error',
                stack:
                  createReqError instanceof Error
                    ? createReqError.stack
                    : undefined,
                name:
                  createReqError instanceof Error
                    ? createReqError.name
                    : undefined,
              })
            );
            throw createReqError;
          }

          // Desktop platforms (Windows, macOS, Linux): use opener + deep-link
          // Mobile platforms (iOS, Android): use web-auth plugin
          if (isDesktopPlatform()) {
            try {
              console.log('Using opener for desktop OAuth flow...');
              await openUrl(req.url);
              // The callback will be handled by the deep-link listener (oauth-callback event)
              console.log('Browser opened, waiting for deep-link callback...');
            } catch (openerError) {
              console.error('Opener failed with error:', openerError);
              const errorMessage =
                openerError instanceof Error
                  ? openerError.message
                  : String(openerError);
              setError(new Error(`Failed to open browser: ${errorMessage}`));
            }
          } else {
            try {
              // Use web-auth plugin which handles ASWebAuthenticationSession on iOS
              // This auto-dismisses the browser after OAuth redirect
              console.log('Using web-auth plugin for mobile OAuth flow...');
              const result = await authenticate({
                url: req.url,
                callbackScheme: getCallbackScheme(),
              });
              console.log(
                'Web-auth completed with callback:',
                result.callbackUrl
              );

              // Process the callback immediately
              await processSigninCallbackFromUrl(result.callbackUrl);
            } catch (webAuthError) {
              console.error('Web-auth plugin failed with error:', webAuthError);
              const errorMessage =
                webAuthError instanceof Error
                  ? webAuthError.message
                  : String(webAuthError);
              setError(new Error(`Authentication failed: ${errorMessage}`));
            }
          }
        } else {
          let req;
          try {
            req = await oidcClient.createSigninRequest({
              state: args?.state || {},
              extraQueryParams: props.oidcConfig.extraQueryParams,
            });
            console.log('Web signin request:', req);
            console.log('Redirecting to:', req.url);
          } catch (createReqError) {
            console.error('Error creating signin request:', createReqError);
            throw createReqError;
          }
          try {
            window.location.href = req.url;
          } catch (redirectError) {
            console.error('Error setting window.location.href:', redirectError);
            throw redirectError;
          }
        }
      } catch (err) {
        console.error('Signin redirect failed with error:', err);
        console.error('Full error object:', err);
        setError(
          err instanceof Error ? err : new Error('Signin redirect failed')
        );
        setActiveNavigator(null);
        recordError(err);
      }
    },
    [oidcClient, isTauri, location.pathname, processSigninCallbackFromUrl]
  );

  // Signout redirect function
  const signoutRedirect = useCallback(
    async (args?: {
      post_logout_redirect_uri?: string;
      onBeforeRedirect?: () => void;
    }) => {
      try {
        setActiveNavigator('signout-redirect');
        setError(null);
        setIsLoggingOut(true);

        // Clear cached data before logout to prevent data leakage between users
        if (isTauri) {
          try {
            await clearAllTables();
            console.log('Successfully cleared all cached data during logout');
          } catch (cacheError) {
            console.warn(
              'Failed to clear cached data during logout:',
              cacheError
            );
          }
        }

        // Clear Redux state BEFORE setting user to null, so the re-render
        // triggered by setUser(null) sees a clean store (no stale data).
        if (args?.onBeforeRedirect) {
          args.onBeforeRedirect();
        }

        // set user to null, making isAuthenticated false
        setUser(null);
        await oidcClient.settings.stateStore.remove(storageKey);

        // Use a dedicated logout redirect URI
        const logoutRedirectUri = `${props.oidcConfig.post_logout_redirect_uri}/logout`;

        console.log('Using logout redirect URI:', logoutRedirectUri);

        // Store logout state in sessionStorage as backup
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('logout_in_progress', 'true');
          sessionStorage.setItem('logout_timestamp', Date.now().toString());

          // For inAppBrowser, we need to clear the flag when the browser closes
          // This is a fallback mechanism
          // Clear any existing timeout first
          if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
          }

          logoutTimeoutRef.current = setTimeout(() => {
            // After 30 seconds, assume logout completed if still in progress
            const stillInProgress =
              sessionStorage.getItem('logout_in_progress');
            if (stillInProgress) {
              sessionStorage.removeItem('logout_in_progress');
              sessionStorage.removeItem('logout_timestamp');
              console.log('Logout timeout - assuming completed');
              // Reset the logout state
              setIsLoggingOut(false);
              setActiveNavigator(null);
            }
            logoutTimeoutRef.current = null;
          }, 30000);
        }

        if (isTauri) {
          const req = await oidcClient.createSignoutRequest({
            post_logout_redirect_uri: logoutRedirectUri,
          });
          console.log('opening url:', req.url);

          // Desktop platforms (Windows, macOS, Linux): use opener + deep-link
          // Mobile platforms (iOS, Android): use web-auth plugin
          if (isDesktopPlatform()) {
            try {
              console.log('Using opener for desktop signout...');
              await openUrl(req.url);
              // The callback will be handled by the deep-link listener (oauth-callback event)
              console.log(
                'Browser opened for logout, waiting for deep-link callback...'
              );
            } catch (openerError) {
              console.error('Opener failed during signout:', openerError);
              const errorMessage =
                openerError instanceof Error
                  ? openerError.message
                  : String(openerError);
              setError(
                new Error(`Failed to open browser for logout: ${errorMessage}`)
              );
              setIsLoggingOut(false);
              setActiveNavigator(null);
            }
          } else {
            try {
              // Use web-auth plugin for logout - it will auto-dismiss when redirect is captured
              console.log('Using web-auth plugin for mobile signout...');
              const result = await authenticate({
                url: req.url,
                callbackScheme: getCallbackScheme(),
              });
              console.log(
                'Web-auth signout completed with callback:',
                result.callbackUrl
              );

              // Clear logout state since we got the callback
              sessionStorage.removeItem('logout_in_progress');
              sessionStorage.removeItem('logout_timestamp');
              if (logoutTimeoutRef.current) {
                clearTimeout(logoutTimeoutRef.current);
                logoutTimeoutRef.current = null;
              }
              setIsLoggingOut(false);
              setActiveNavigator(null);
            } catch (webAuthError) {
              console.error(
                'Web-auth plugin failed during signout:',
                webAuthError
              );
              const errorMessage =
                webAuthError instanceof Error
                  ? webAuthError.message
                  : String(webAuthError);
              setError(new Error(`Logout failed: ${errorMessage}`));
              setIsLoggingOut(false);
              setActiveNavigator(null);
            }
          }
        } else {
          const req = await oidcClient.createSignoutRequest({
            post_logout_redirect_uri: logoutRedirectUri,
          });
          window.location.href = req.url;
        }
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Signout redirect failed')
        );
        setActiveNavigator(null);
        setIsLoggingOut(false);
        recordError(err);
      }
      // Remove the finally block - we'll handle logout completion via URL params
    },
    [oidcClient, isTauri, props.oidcConfig.post_logout_redirect_uri]
  );

  // Silent logout - clears local state and ends OIDC session via iframe (no redirect)
  // For web apps, after silent logout we redirect to login (don't try silent signin,
  // as the OIDC session might still be cached briefly)
  const signoutSilent = useCallback(
    async (args?: { onBeforeRedirect?: () => void }) => {
      try {
        setActiveNavigator('signout-silent');
        setError(null);
        setIsLoggingOut(true);

        // Clear cached data before logout to prevent data leakage between users
        if (isTauri) {
          try {
            await clearAllTables();
            console.log(
              'Successfully cleared all cached data during silent logout'
            );
          } catch (cacheError) {
            console.warn(
              'Failed to clear cached data during silent logout:',
              cacheError
            );
          }
        }

        // Clear Redux state BEFORE setting user to null, so the re-render
        // triggered by setUser(null) sees a clean store (no stale data).
        if (args?.onBeforeRedirect) {
          args.onBeforeRedirect();
        }

        // Clear local user state
        setUser(null);
        await oidcClient.settings.stateStore.remove(storageKey);

        // For web apps, try to end the OIDC session via iframe (silent)
        // This clears the session cookie on the OIDC provider without redirecting
        if (!isTauri) {
          try {
            const req = await oidcClient.createSignoutRequest({
              post_logout_redirect_uri:
                props.oidcConfig.post_logout_redirect_uri,
            });

            // Create hidden iframe to end session
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.setAttribute('aria-hidden', 'true');

            await new Promise<void>((resolve) => {
              const timeout = setTimeout(() => {
                console.log(
                  'Silent logout iframe timed out (session may still be ended)'
                );
                resolve();
              }, 5000);

              iframe.onload = () => {
                clearTimeout(timeout);
                console.log('Silent logout iframe loaded');
                resolve();
              };

              iframe.onerror = () => {
                clearTimeout(timeout);
                console.log(
                  'Silent logout iframe error (session may still be ended)'
                );
                resolve();
              };

              document.body.appendChild(iframe);
              iframe.src = req.url;
            });

            // Clean up iframe
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }

            // After silent logout on web, redirect to login immediately
            // Don't rely on auto-redirect with silent signin - the OIDC session
            // cookie might still be briefly cached, causing silent signin to succeed
            const signinReq = await oidcClient.createSigninRequest({
              state: { returnUrl: '/' },
              extraQueryParams: props.oidcConfig.extraQueryParams,
            });
            window.location.href = signinReq.url;
            return; // Don't reset isLoggingOut - we're redirecting away
          } catch (silentLogoutError) {
            // Silent logout failed, but local state is cleared - that's okay
            console.log(
              'Silent logout iframe failed, but local state cleared:',
              silentLogoutError
            );
          }
        }

        // For Tauri, we just clear local state (no iframe needed)
        // Force the next signin to show the login form instead of auto-authenticating
        // Persisted in localStorage so it survives app restarts
        forceLoginPromptRef.current = true;
        localStorage.setItem(FORCE_LOGIN_KEY, 'true');
        console.log('Local logout completed, next signin will use prompt=login');
      } catch (err) {
        console.error('Silent logout error:', err);
        setError(
          err instanceof Error ? err : new Error('Silent logout failed')
        );
        recordError(err);
      } finally {
        setIsLoggingOut(false);
        setActiveNavigator(null);
      }
    },
    [
      oidcClient,
      isTauri,
      props.oidcConfig.post_logout_redirect_uri,
      props.oidcConfig.extraQueryParams,
      storageKey,
    ]
  );

  // Signin popup function (fallback to redirect)
  const signinPopup = useCallback(async () => {
    return signinRedirect();
  }, [signinRedirect]);

  const authState: CustomAuthState = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    sessionExpired,
    signinRedirect,
    signinSilent,
    signoutRedirect,
    signoutSilent,
    signinPopup,
    activeNavigator,
    isLoggingOut,
  };

  return (
    <customAuthContext.Provider value={authState}>
      {props.children}
    </customAuthContext.Provider>
  );
}

/**
 * This provides our custom context for the app. Should not be used outside of this module.
 */
function AuthContextProvider(props: {
  children: React.ReactNode;
  oidcConfig: OidcConfig;
}) {
  const auth = useContext(customAuthContext);
  const dispatch = useDispatch();
  const location = useLocation();

  if (!auth) {
    throw new Error(
      'AuthContextProvider must be used within CustomAuthProvider'
    );
  }

  const currentUser = useMemo(
    () =>
      auth.user?.access_token
        ? decodeJwtAugmented(auth.user.access_token)
        : null,
    [auth.user?.access_token]
  );

  const logout = useCallback(async () => {
    // Create onBeforeRedirect function to clear Redux state
    const onBeforeRedirect = () => {
      try {
        // Only dispatch if the logout action exists
        if (authActions.asyncActions.logout) {
          dispatch(authActions.asyncActions.logout() as any);
          console.log('Successfully cleared Redux state during logout');
        }
      } catch (reduxError) {
        console.warn('Failed to clear Redux state during logout:', reduxError);
      }
    };

    const isTauri = (window as any).__TAURI__ !== undefined;
    if (isTauri && !isDesktopPlatform()) {
      // Mobile: clear local state silently, next signin will use prompt=login
      // to force the IdP to show the login form
      await auth.signoutSilent({ onBeforeRedirect });
    } else {
      // Desktop/Web: redirect to IdP's end-session endpoint
      await auth.signoutRedirect({ onBeforeRedirect });
    }
  }, [auth.signoutRedirect, auth.signoutSilent, dispatch]);

  useEffect(() => {
    if (auth.error) {
      trackEvent('AuthenticationError', {
        message: auth.error?.message || 'Unknown error',
      });
      recordError(auth.error);
    }
  }, [auth.error]);

  useEffect(() => {
    const isTauri = (window as any).__TAURI__ !== undefined;

    // Capture current URL to redirect back after login
    // Don't capture callback/logout URLs
    const currentPath = location.pathname + location.search + location.hash;
    const shouldCaptureUrl =
      !currentPath.includes('/authentication/') &&
      !currentPath.includes('/logout');
    const returnUrl = shouldCaptureUrl ? currentPath : '/';

    // Detect if the user needs to login, then redirect them
    if (!auth.isAuthenticated && !auth.isLoading && !auth.isLoggingOut) {
      // On web: always redirect to login (even if there was an error)
      // On Tauri: only redirect if no error (show error screen with retry button instead)
      if (!isTauri || !auth.error) {
        // Pass returnUrl through OAuth state - it will be available as userState.returnUrl after callback
        auth.signinRedirect({ state: { returnUrl } });
      }
    }
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    auth.isLoggingOut,
    auth.error,
    auth.signinRedirect,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (auth.user?.access_token) {
      dispatch(authActions.setAccessToken(auth.user.access_token));
      v14.addBearerToken(auth.user.access_token);
      setAccessToken(auth.user.access_token);
    }
  }, [auth.user?.access_token, dispatch]);

  // Clear Redux state when user logs out (backup - in case proactive clearing didn't happen)
  useEffect(() => {
    if (
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.isLoggingOut &&
      auth.user === null
    ) {
      // User has logged out, clear Redux state as backup
      try {
        // Only dispatch if the logout action exists
        if (authActions.asyncActions.logout) {
          dispatch(authActions.asyncActions.logout() as any);
          console.log('Successfully cleared Redux state after logout (backup)');
        }
      } catch (reduxError) {
        console.warn(
          'Failed to clear Redux state after logout (backup):',
          reduxError
        );
      }
    }
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    auth.isLoggingOut,
    auth.user,
    dispatch,
  ]);

  const ability = useMemo(() => {
    if (currentUser) {
      return defineRules(
        currentUser.role as Role,
        currentUser.userScopes as Scope[]
      );
    }

    return null;
  }, [currentUser]);

  return (
    <authContext.Provider
      value={{
        user: currentUser,
        logout,
      }}
    >
      <tokenContext.Provider value={currentUser}>
        <AbilityContext.Provider value={ability}>
          {props.children}
        </AbilityContext.Provider>
      </tokenContext.Provider>
    </authContext.Provider>
  );
}

// This is a workaround to prevent the issue where refresh tokens are shared across tabs/windows when clicking "Duplicate Tab".
// If they're shared, then theres a chance a refresh token will get used twice.
// IMPORTANT NOTE AND LESSIN: If a refresh token is attempted to be used twice, the whole grant is revoked, meaning the user is logged out from everywhere that started from the same point; the whole chain of tokens is revoked.
// https://github.com/panva/node-oidc-provider/blob/main/docs/README.md#rotaterefreshtoken
try {
  window.addEventListener('beforeunload', function (event) {
    window.sessionStorage.removeItem('__lock');
  });

  // Also use pagehide for iOS Safari where beforeunload doesn't fire
  window.addEventListener('pagehide', function (event) {
    window.sessionStorage.removeItem('__lock');
  });

  // Check if we're on an OAuth callback page - if so, don't clear storage
  // iOS Safari doesn't fire beforeunload/pagehide on redirects, so the lock
  // may still be present when returning from OAuth. We need to preserve
  // the OIDC state (stored with "oidc." prefix) for the callback to work.
  const isOAuthCallback = window.location.pathname.includes('/authentication/');

  if (window.sessionStorage.getItem('__lock') && !isOAuthCallback) {
    window.sessionStorage.clear();
    console.warn('Found a lock in session storage. The storage was cleared.');
  }

  window.sessionStorage.setItem('__lock', '1');
} catch (e) {}

/**
 * Use this to provide OIDC authentication to an application.
 * You'll need to use it if you want to use AuthenticatedSection and useReadCloudAuth.
 *
 * Also provides "AuthORIZATION" through casl-react, which tells children about the user's permissions.
 */
export function AuthProvider(props: {
  authority: string;
  client_id: string;
  children: React.ReactNode;
  signinCallback?: () => void;
  redirect_uri: string;
  post_logout_redirect_uri?: string;
  response_type?: string;
  scope?: string;
  silent_redirect_uri?: string;
  automaticSilentRenew?: boolean;
  extraQueryParams?: Record<string, string>;
}) {
  // Memoised: CustomAuthProvider builds its OidcClient from this object and
  // (re)loads the stored user whenever it changes, so a fresh object on every
  // render would re-run startup — including token refreshes — for nothing.
  const oidcConfig = useMemo<OidcConfig>(
    () => ({
      authority: props.authority,
      client_id: props.client_id,
      redirect_uri: props.redirect_uri ?? window.location.origin,
      response_type: props.response_type ?? 'code',
      scope: props.scope ?? 'openid profile email offline_access',
      post_logout_redirect_uri:
        props.post_logout_redirect_uri ?? window.location.origin,
      silent_redirect_uri: props.silent_redirect_uri,
      automaticSilentRenew: props.automaticSilentRenew,
      extraQueryParams: props.extraQueryParams,
    }),
    [
      props.authority,
      props.client_id,
      props.redirect_uri,
      props.response_type,
      props.scope,
      props.post_logout_redirect_uri,
      props.silent_redirect_uri,
      props.automaticSilentRenew,
      props.extraQueryParams,
    ]
  );

  return (
    <CustomAuthProvider oidcConfig={oidcConfig}>
      <AuthContextProvider oidcConfig={oidcConfig}>
        {props.children}
      </AuthContextProvider>
    </CustomAuthProvider>
  );
}

/**
 * Use this to access ReadCloud's authentication context.
 * Must be used underneath an AuthProvider
 */
export function useReadCloudAuth() {
  const ctx = useContext(authContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

/**
 * Use this to block off a section of your app that requires authentication.
 * This will show a loading indicator while the user is being authenticated.
 * If the user is not authenticated, they will be redirected to the login page.
 */
export function AuthenticatedSection(props: { children: React.ReactNode }) {
  const auth = useContext(customAuthContext);
  const [sessionExpiredDismissed, setSessionExpiredDismissed] = useState(false);
  const [showManualAction, setShowManualAction] = useState(false);

  if (!auth) {
    throw new Error('AuthenticatedSection must be used within an AuthProvider');
  }

  // Show manual action button after 3 seconds if stuck
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading && !auth.isLoggingOut) {
      const timer = setTimeout(() => {
        setShowManualAction(true);
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setShowManualAction(false);
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.isLoggingOut]);

  // Determine the current auth stage and message
  const getAuthStageInfo = () => {
    if (auth.isLoggingOut) {
      return {
        message: 'Logging out...',
        manualAction: 'Retry Logout',
        onManualAction: () => auth.signoutRedirect(),
        showManualAfter: 2000, // Show manual logout button sooner
      };
    }

    if (auth.activeNavigator === 'signin-silent') {
      return {
        message: 'Checking session...',
        manualAction: 'Sign In',
        onManualAction: () => auth.signinRedirect(),
        showManualAfter: 5000, // Give more time for silent signin
      };
    }

    if (auth.activeNavigator === 'signin-redirect') {
      return {
        message: 'Redirecting to sign in...',
        manualAction: 'Open Sign In',
        onManualAction: () => auth.signinRedirect(),
        showManualAfter: 3000,
      };
    }

    if (auth.activeNavigator === 'signin-callback') {
      return {
        message: 'Verifying user...',
        manualAction: 'Retry Verification',
        onManualAction: () => auth.signinRedirect(),
        showManualAfter: 5000, // Give more time for verification
      };
    }

    if (auth.activeNavigator === 'token-refresh') {
      return {
        message: 'Verifying user...',
        manualAction: 'Sign In',
        onManualAction: () => auth.signinRedirect(),
        showManualAfter: 5000,
      };
    }

    if (auth.activeNavigator === 'signout-redirect') {
      return {
        message: 'Logging out...',
        manualAction: 'Retry Logout',
        onManualAction: () => auth.signoutRedirect(),
        showManualAfter: 2000,
      };
    }

    if (auth.activeNavigator === 'signout-silent') {
      return {
        message: 'Logging out...',
        manualAction: 'Retry Logout',
        onManualAction: () => auth.signoutRedirect(),
        showManualAfter: 2000,
      };
    }

    // Default case - not authenticated and no active navigator
    return {
      message: 'Redirecting to sign in...',
      manualAction: 'Sign In',
      onManualAction: () => auth.signinRedirect(),
      showManualAfter: 3000,
    };
  };

  const authStageInfo = getAuthStageInfo();

  // Show manual action button based on the stage
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading) {
      const timer = setTimeout(() => {
        setShowManualAction(true);
      }, authStageInfo.showManualAfter);

      return () => clearTimeout(timer);
    } else {
      setShowManualAction(false);
    }
  }, [auth.isAuthenticated, auth.isLoading, authStageInfo.showManualAfter]);

  if (auth.isAuthenticated && auth.user) {
    // Only the "expired while the IdP was unreachable" case gets a dialog: the
    // user can keep working with on-device content or sign in again. Any other
    // error while signed in is recorded (see AuthContextProvider) but must not
    // interrupt a working session — and a *rejected* refresh never reaches
    // here, because it clears the user and redirects to login instead.
    return (
      <>
        {auth.sessionExpired && !sessionExpiredDismissed && (
          <div className="rc-fixed rc-inset-0 rc-bg-black rc-bg-opacity-50 rc-flex rc-items-center rc-justify-center rc-z-50">
            <div className="rc-bg-white rc-rounded-lg rc-p-6 rc-max-w-md rc-w-full rc-mx-4 rc-shadow-xl">
              <div className="rc-text-center rc-space-y-4">
                <div className="rc-text-lg rc-font-semibold rc-text-gray-900">
                  Your session has expired.
                </div>
                <div className="rc-text-sm rc-text-gray-600">
                  We couldn't reach the sign-in server to renew it. You can keep
                  working with content already on this device, or sign in again
                  once you're back online.
                </div>
                <div className="rc-space-y-2">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      return auth.signinPopup();
                    }}
                    className="rc-block rc-w-full rc-px-4 rc-py-2 rc-bg-blue-500 rc-text-white rc-rounded-md rc-hover:bg-blue-600 rc-transition-colors rc-duration-200 rc-text-center rc-text-sm rc-font-medium"
                  >
                    Sign in again
                  </a>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setSessionExpiredDismissed(true);
                    }}
                    className="rc-block rc-w-full rc-px-4 rc-py-2 rc-bg-gray-300 rc-text-gray-700 rc-rounded-md rc-hover:bg-gray-400 rc-transition-colors rc-duration-200 rc-text-center rc-text-sm rc-font-medium"
                  >
                    Continue working
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
        {props.children}
      </>
    );
  } else {
    // Show error UI if there's an auth error during login (Tauri apps only)
    // On web, the auto-redirect will handle it - don't show error screen
    const isTauri = (window as any).__TAURI__ !== undefined;
    if (auth.error && isTauri) {
      return (
        <div className="rc-fixed rc-inset-0 rc-flex rc-flex-col rc-justify-center rc-items-center rc-bg-gray-50">
          <div className="rc-bg-white rc-rounded-lg rc-p-6 rc-max-w-md rc-w-full rc-mx-4 rc-shadow-xl">
            <div className="rc-text-center rc-space-y-4">
              <div className="rc-text-lg rc-font-semibold rc-text-gray-900">
                You must log in to see your content
              </div>
              <button
                onClick={() => auth.signinRedirect()}
                className="rc-w-full rc-px-4 rc-py-2 rc-bg-blue-500 rc-text-white rc-rounded-md rc-hover:bg-blue-600 rc-transition-colors rc-duration-200 rc-text-sm rc-font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="rc-fixed rc-inset-0 rc-flex rc-flex-col rc-justify-center rc-items-center rc-bg-gray-50">
        <div className="rc-w-8 rc-h-8 rc-border-4 rc-border-blue-500 rc-rounded-full rc-animate-spin rc-border-t-transparent"></div>

        {!showManualAction && (
          <div className="rc-mt-5 rc-text-center rc-text-gray-500 rc-text-base">
            {authStageInfo.message}
          </div>
        )}

        {showManualAction && (
          <div className="rc-mt-5 rc-text-center rc-animate-in rc-fade-in rc-slide-in-from-bottom-2 rc-duration-500">
            <div className="rc-mb-4 rc-text-gray-500 rc-text-base">
              Taking too long? {authStageInfo.manualAction} manually below.
            </div>
            <button
              onClick={authStageInfo.onManualAction}
              className="rc-px-4 rc-py-2 rc-bg-blue-500 rc-text-white rc-rounded-md rc-hover:bg-blue-600 rc-transition-colors rc-duration-200 rc-font-semibold rc-text-sm"
            >
              {authStageInfo.manualAction}
            </button>
          </div>
        )}
      </div>
    );
  }
}
